import { yupResolver } from '@hookform/resolvers';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import cn from 'classnames';
import dayjs from 'dayjs';
// Types
import { OptionTypeBase } from 'react-select';
// Hooks
import { useGetScheduleTimeZones } from 'hooks';
// Helpers
import {
  CheckboxState,
  createDateTimeObject,
  Weekdays,
  TimeRanges,
  getTimezoneOptions,
  getPickerValue,
} from '../helpers';
// UI
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
import Input from 'ui3/Input/Input';
import DatePicker from 'ui3/DatePicker/DatePicker';
import Select from 'ui3/Select/Select';
import Text from 'ui3/Text/Text';
// Styles
import styles from './WeeklyHoursForm.module.scss';

const validationSchema = yup.object().shape({
  minimumNoticeMinutes: yup
    .number()
    .required('Please add number')
    .typeError('Must be a number greater than 0'),
  durationMinutes: yup.object({
    value: yup.string(),
    label: yup.string(),
  }),
});

type FormInputs = {
  minimumNoticeMinutes: number;
  durationMinutes: OptionTypeBase;
};

const DURATION_MINUTES: OptionTypeBase[] = [
  { label: '15 minutes', value: 15 },
  { label: '30 minutes', value: 30 },
  { label: '60 minutes', value: 60 },
];

type WeeklyHoursFormProps = {
  checkboxState: CheckboxState;
  timeRanges: TimeRanges;
  minimumNotice: number;
  durationMinutes: number;
  selectedTimezone: string;
  setCheckboxState: (value: CheckboxState) => void;
  setTimezone: (value: string) => void;
  setTimeRanges: (value: TimeRanges) => void;
  setMinimumNotice: (value: number) => void;
  setDurationMinutes: (value: number) => void;
  onValidationChange: (isValid: boolean) => void;
};

const WeeklyHoursForm = ({
  selectedTimezone,
  checkboxState,
  timeRanges,
  minimumNotice,
  durationMinutes,
  setCheckboxState,
  setTimeRanges,
  setTimezone,
  setMinimumNotice,
  setDurationMinutes,
  onValidationChange,
}: WeeklyHoursFormProps): JSX.Element => {
  const handleTimeChange = (
    day: keyof CheckboxState,
    index: number,
    value: [dayjs.Dayjs | null, dayjs.Dayjs | null]
  ) => {
    const [from, to] = value || [null, null];
    const newRanges = [...timeRanges[day]];
    newRanges[index] = { from, to };
    setTimeRanges({ ...timeRanges, [day]: newRanges });
  };
  const { data: timeZonesData } = useGetScheduleTimeZones();
  const timezoneOptions = getTimezoneOptions(timeZonesData);

  const formattedTimezoneOptions = timezoneOptions.map((item) => ({
    ...item,
    oldTzName: item.label,
    label: item.label.replace('_', ' '),
  }));

  const defaultValueTz = formattedTimezoneOptions.find((option) => {
    if (option.oldTzName === selectedTimezone) {
      return option.label;
    }
  });

  const handleAddTimeRange = (day: keyof CheckboxState) => {
    setTimeRanges({
      ...timeRanges,
      [day]: [...timeRanges[day], { from: null, to: null }],
    });
  };

  const handleRemoveTimeRange = (day: keyof CheckboxState, index: number) => {
    setTimeRanges({
      ...timeRanges,
      [day]: timeRanges[day].filter((_, i) => i !== index),
    });
  };

  const handleTimezoneChange = (e) => {
    const newTimezone = e.oldTzName;
    setTimezone(newTimezone);
  };

  const handleSetCheckboxesState = (
    day: keyof CheckboxState,
    value: boolean
  ) => {
    setCheckboxState({
      ...checkboxState,
      [day]: value,
    });
  };

  const durationMinutesDefaultValue = DURATION_MINUTES.find(
    (option) => Number(option.value) === durationMinutes
  );

  const { register, errors: formErrors, setValue } = useForm<FormInputs>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  });

  useEffect(() => {
    if (durationMinutesDefaultValue) {
      setValue('durationMinutes', durationMinutesDefaultValue);
    }
  }, [durationMinutesDefaultValue, setValue]);

  useEffect(() => {
    if (minimumNotice) {
      setValue('minimumNoticeMinutes', minimumNotice);
    }
  }, [minimumNotice, setValue]);

  useEffect(() => {
    const selectedDays = Object.entries(checkboxState).filter(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ([_, isChecked]) => isChecked
    );

    // No error if we have an empty schedule
    if (selectedDays.length === 0) {
      onValidationChange(true);
      return;
    }

    // Validate that each date entry has valid time ranges
    const isValid = selectedDays.every(([day]) => {
      const ranges = timeRanges[day as keyof TimeRanges];
      return ranges.every((range) => range.from && range.to);
    });

    onValidationChange(isValid);
  }, [checkboxState, timeRanges, onValidationChange]);

  const handleDurationChange = (e) => {
    setDurationMinutes(e.value);
  };

  return (
    <div className={styles.root}>
      <div className={styles.titleBlock}>
        <Text variant="subtitle2" color="lights-high">
          Weekly Hours
        </Text>
        <Text variant="body2Regular" color="lights-low">
          Set your weekly hours of availability so interview guests know when
          you&apos;re typically available.
        </Text>
      </div>

      <div className={styles.formWrapper}>
        <Input
          label="Minimum notice in minutes"
          name="minimumNoticeMinutes"
          id="minimumNoticeMinutes"
          ref={register}
          type="number"
          min="0"
          error={formErrors?.minimumNoticeMinutes?.message}
          onChange={(e) => setMinimumNotice(Number(e.target.value))}
        />

        <Select
          label="Duration in minutes"
          labelClassName={styles.labelStyle}
          placeholder="Select interview duration"
          options={DURATION_MINUTES}
          value={durationMinutesDefaultValue}
          id="durationMinutes"
          name="durationMinutes"
          inputId="durationMinutes"
          fieldSize="large"
          onChange={(e) => handleDurationChange(e)}
        />

        <Select
          id="timeZone"
          name="timeZone"
          inputId="timeZone"
          label="Time Zone"
          value={defaultValueTz}
          className={styles.timeZoneSelect}
          options={formattedTimezoneOptions}
          onChange={handleTimezoneChange}
          isSearchable={false}
        />
      </div>

      <div className={styles.weekly}>
        <ul
          className={styles.daysCheckbox}
          role="group"
          aria-label="Days of the week"
        >
          {(Object.keys(checkboxState) as Weekdays[]).map((day) => {
            return (
              <li key={'key_' + day}>
                <button
                  type="button"
                  className={cn(styles.checkbox, {
                    [styles.checkedCheckbox]: checkboxState[day],
                  })}
                  name={'day_' + day}
                  aria-label={day.charAt(0).toUpperCase() + day.slice(1)}
                  aria-pressed={checkboxState[day]}
                  onClick={() => {
                    const newState = !checkboxState[day];
                    handleSetCheckboxesState(day, newState);
                  }}
                >
                  {day.charAt(0).toUpperCase()}
                </button>
              </li>
            );
          })}
        </ul>

        {(Object.keys(checkboxState) as Weekdays[])
          .filter((day) => checkboxState[day])
          .map((day) => {
            const lastItem = timeRanges[day].length - 1;

            return (
              <div key={day} className={styles.day}>
                {timeRanges[day].map((range, index) => {
                  const timeFrom = createDateTimeObject(range.from);
                  const timeTo = createDateTimeObject(range.to);

                  const minTime = timeFrom
                    ? dayjs(dayjs(timeFrom).add(15, 'minutes')).toDate()
                    : undefined;

                  const maxTime = dayjs().endOf('day').toDate();

                  return (
                    <div
                      key={'day_' + index}
                      className={styles.timeRangeWrapper}
                    >
                      <p className={styles.dayName}>
                        {index === 0
                          ? day.charAt(0).toUpperCase() + day.slice(1)
                          : ''}
                      </p>
                      <div className={styles.timeRange}>
                        <DatePicker
                          className={styles.timeInput}
                          showTimeSelect
                          showTimeSelectOnly
                          timeIntervals={15}
                          timeCaption="Time"
                          dateFormat="h:mm aa"
                          selected={timeFrom}
                          onChange={(e) =>
                            handleTimeChange(day, index, [
                              getPickerValue(e),
                              range.to,
                            ])
                          }
                          placeholderText="Start time"
                          portalId="weekly-hours-timepicker-portal"
                          popperClassName={styles.timePickerPopper}
                          customInput={
                            <Input
                              className={cn(styles.timeInput, {
                                [styles.errorInput]: !range.from,
                              })}
                              name={`${day}_${index}_from`}
                              inputMode="none"
                            />
                          }
                        />
                        <p className={styles.timeSeparator}>to</p>

                        <DatePicker
                          className={styles.timeInput}
                          showTimeSelect
                          showTimeSelectOnly
                          timeIntervals={15}
                          timeCaption="Time"
                          dateFormat="h:mm aa"
                          selected={timeTo}
                          onChange={(e) =>
                            handleTimeChange(day, index, [
                              range.from,
                              getPickerValue(e),
                            ])
                          }
                          placeholderText="End time"
                          minTime={minTime}
                          maxTime={maxTime}
                          disabled={!(minTime && maxTime)}
                          portalId="weekly-hours-timepicker-portal"
                          popperClassName={styles.timePickerPopper}
                          customInput={
                            <Input
                              className={cn(styles.timeInput, {
                                [styles.errorInput]: !range.to,
                              })}
                              name={`${day}_${index}_to`}
                              inputMode="none"
                            />
                          }
                        />
                      </div>
                      <div className={styles.iconWrapper}>
                        {index === lastItem && (
                          <button
                            type="button"
                            className={styles.iconButton}
                            aria-label={`Add time range for ${day}`}
                            onClick={() => handleAddTimeRange(day)}
                          >
                            <MillionsIcon
                              className={styles.icon}
                              name="plusCircleOutline"
                            />
                          </button>
                        )}
                        {index > 0 && (
                          <button
                            type="button"
                            className={styles.iconButton}
                            aria-label={`Remove time range ${
                              index + 1
                            } for ${day}`}
                            onClick={() => handleRemoveTimeRange(day, index)}
                          >
                            <MillionsIcon
                              className={styles.icon}
                              name="minusCircle"
                            />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default WeeklyHoursForm;
